import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled, { type CSSProperties } from 'styled-components';

import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { ensureHexColor } from '@sorare/core/src/lib/colors';

import type {
  TeamSlot_anyCardInterface,
  TeamSlot_cardCollection,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  width: 100%;
  height: 100%;
  border-radius: var(--unit);
  position: relative;
  border: 1px solid var(--c-nd-50);
  padding: var(--half-unit);
  object-fit: contain;
  isolation: isolate;
  overflow: hidden;
`;

const TeamLogo = styled.img`
  width: 32px;
  object-fit: contain;
`;

const ShortName = styled(LabelXS)`
  text-overflow: ellipsis;
  overflow: hidden;
  width: 100%;
  text-align: center;
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const StyledCircularProgress = styled(CircularProgress)`
  width: 12px;
  height: 12px;
  --circular-progress-stroke: var(--c-nd-900-opaque);
`;
const SlotsCount = styled(Horizontal)`
  white-space: nowrap;
`;

type Props = {
  cardCollection: Nullable<TeamSlot_cardCollection>;
  newCards: TeamSlot_anyCardInterface[];
  animatingCardIndex: Nullable<number>;
};

export const TeamSlot = ({
  cardCollection,
  newCards,
  animatingCardIndex,
}: Props) => {
  const { anyTeam, userCardCollection } = cardCollection || {};
  const { slotsCount } = userCardCollection || {};
  const { pictureUrl, shortName, styleGuideColor } = anyTeam || {};
  const color = ensureHexColor(styleGuideColor);
  const nonAnimatedCardsCount =
    newCards.filter(card => card.anyTeam?.slug === anyTeam?.slug).length -
    (typeof animatingCardIndex === 'number' ? animatingCardIndex + 1 : 0);
  const totalCollectedCount = cardCollection?.userCardCollection.slots.filter(
    slot => !!slot.cardCollectionCardsCount
  ).length;
  const collectedCount = Math.max(
    (totalCollectedCount || 0) - nonAnimatedCardsCount,
    0
  );
  return (
    <Wrapper center>
      <StyledTextureBackground
        background="missions"
        color={['#000000', color]}
        imageOpacity={0.9}
      />
      <TeamLogo src={pictureUrl || undefined} alt="" />
      <ShortName bold className="text-center">
        {shortName}
      </ShortName>
      <Horizontal center gap={0.5}>
        <SlotsCount gap={0.5} style={{ alignItems: 'flex-end' }}>
          <LabelS bold>
            <AnimatedNumber value={collectedCount} />
          </LabelS>
          <LabelXS color="var(--c-nd-600)">/ {slotsCount}</LabelXS>
        </SlotsCount>
        <StyledCircularProgress
          progress={collectedCount}
          maxProgress={slotsCount || 0}
          style={
            {
              '--circular-progress-stroke': styleGuideColor,
            } as CSSProperties
          }
        />
      </Horizontal>
    </Wrapper>
  );
};

TeamSlot.fragments = {
  cardCollection: gql`
    fragment TeamSlot_cardCollection on CardCollection {
      slug
      userCardCollection(forUserSlug: $user) {
        id
        slotsCount
        slots {
          slug
          cardCollectionCardsCount
        }
      }
      anyTeam {
        slug
        shortName
        pictureUrl(derivative: "avatar")
        styleGuideColor
      }
    }
  ` as TypedDocumentNode<TeamSlot_cardCollection>,
  anyCardInterface: gql`
    fragment TeamSlot_anyCardInterface on AnyCardInterface {
      slug
      anyTeam {
        slug
      }
    }
  ` as TypedDocumentNode<TeamSlot_anyCardInterface>,
};
